import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, Save, Stethoscope, X } from 'lucide-react';
import { SearchableSelect } from './searchable-select';
import { useCreateDoctor, useDepartments, useActiveDesignations, useActiveSpecializations } from '../../services/httpServices/queries';
import { doctorService } from '../../services/httpServices/doctorService';

interface QuickDoctorModalProps {
  open: boolean;
  /** Text typed into the doctor search, prefilled into the form. */
  initialQuery?: string;
  onClose: () => void;
  onCreated: (doctor: { id: string; name?: string }) => void;
}

const inputClass =
  'w-full px-3 py-2 rounded-lg border border-border bg-background text-text placeholder:text-text-muted/60 focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-colors';
const labelClass = 'block text-xs font-medium text-text-muted mb-1';

/**
 * Minimal "add an outside doctor" dialog. Registers a new `doctor` employee so
 * they can be picked as a reference-only doctor on a receipt without leaving the
 * counter. The created doctor is returned to the caller so the field can select
 * it immediately.
 */
export function QuickDoctorModal({ open, initialQuery = '', onClose, onCreated }: QuickDoctorModalProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [designationId, setDesignationId] = useState('');
  const [specializationId, setSpecializationId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const createDoctorMutation = useCreateDoctor();
  const { data: departmentsData } = useDepartments();
  const { data: designationsData } = useActiveDesignations();
  const { data: specializationsData } = useActiveSpecializations();

  // Reset + prefill whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    setName(initialQuery || '');
    setPhone(/^[+\d][\d\s+-]*$/.test(initialQuery) ? initialQuery : '');
    setDepartmentId('');
    setDesignationId('');
    setSpecializationId('');
    setError(null);
  }, [open, initialQuery]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleSave = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Doctor name is required.');
      return;
    }
    setSaving(true);
    try {
      // Guard against an obvious duplicate before creating a new employee.
      const existing = await doctorService.getDoctors(1, 10, { search: phone.trim() || name.trim() });
      const match = existing.data?.find(
        (d: any) =>
          d.name?.toLowerCase() === name.trim().toLowerCase() ||
          (phone.trim() && d.phone === phone.trim()),
      );
      if (match) {
        setError(`A doctor named "${match.name}" already exists. Please select from the list.`);
        return;
      }
      const result = await createDoctorMutation.mutateAsync({
        name: name.trim(),
        phone: phone.trim() || undefined,
        departmentId: departmentId || undefined,
        designationId: designationId || undefined,
        specializationIds: specializationId ? [specializationId] : undefined,
        revenueSharePercent: 0,
        type: 'doctor',
        isActive: true,
      });
      if (result.data?.id) {
        onCreated({ id: result.data.id, name: result.data.name });
      } else {
        setError('Failed to create doctor. Please try again.');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to create doctor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 modal-overlay-enter">
      <div className="bg-surface rounded-2xl border border-border shadow-2xl w-full max-w-lg max-h-[88vh] flex flex-col modal-panel-enter">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-background/50 rounded-t-2xl shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Stethoscope size={18} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-text truncate">Add Doctor</h2>
              <p className="text-xs text-text-muted truncate">
                Quick-register a doctor without leaving the receipt
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 hover:bg-background rounded-lg transition-colors shrink-0"
          >
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {error && (
            <div className="flex items-start gap-2 p-2.5 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
              <AlertCircle size={15} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label className={labelClass} htmlFor="quick-doctor-name">
              Doctor Name *
            </label>
            <input
              id="quick-doctor-name"
              type="text"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Dr. Md. Rafiqul Islam"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass} htmlFor="quick-doctor-phone">
              Phone / Mobile
            </label>
            <input
              id="quick-doctor-phone"
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+8801…"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Department</label>
            <SearchableSelect
              value={departmentId}
              onChange={setDepartmentId}
              options={(departmentsData?.data || []).map((d: any) => ({ value: d.id, label: d.name }))}
              placeholder="Select department..."
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Designation</label>
              <SearchableSelect
                value={designationId}
                onChange={setDesignationId}
                options={(designationsData?.data || []).map((d: any) => ({ value: d.id, label: d.name }))}
                placeholder="Select..."
              />
            </div>
            <div>
              <label className={labelClass}>Specialization</label>
              <SearchableSelect
                value={specializationId}
                onChange={setSpecializationId}
                options={(specializationsData?.data || []).map((s: any) => ({ value: s.id, label: s.label || s.name }))}
                placeholder="Select..."
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3 shrink-0 bg-background/50 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-2 text-text-muted hover:text-text transition-colors text-sm flex items-center gap-2"
          >
            <X size={16} />
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save Doctor
          </button>
        </div>
      </div>
    </div>
  );
}
